// Helpers for the saved run activity log (pure, so they are easy to test).

export const LEVELS = ['all', 'warning', 'error'];

/** Entries to show for a filter: "warning" = warnings and errors, "error" = errors only. */
export function filterEntries(entries, level) {
  const list = Array.isArray(entries) ? entries : [];
  if (level === 'error') return list.filter((e) => e.level === 'error');
  if (level === 'warning') return list.filter((e) => e.level === 'warning' || e.level === 'error');
  return list;
}

export function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Number(seconds);
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}

export function formatMs(ms) {
  if (ms == null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms} ms`;
}

/** Every HTTP call in the run (the run-level ones plus those recorded against pairs), slowest first. */
export function collectCalls(entries, pairs) {
  const rows = [];
  (entries || []).forEach((e) => {
    if (e.kind === 'http_call' && e.url) {
      rows.push({
        ...e.url,
        sample_ref: e.sample_ref || '',
        attr_ref: e.attr_ref || '',
        phase: e.detail?.phase || '',
        model: e.detail?.model || '',
        error: e.detail?.error || '',
        detail: e.detail || {},
        ts: e.ts,
      });
    }
  });
  return rows.sort((a, b) => (b.duration_ms || 0) - (a.duration_ms || 0));
}

export function pairLabel(p) {
  return `${p.sample_ref || '?'} · ${p.attr_ref || '?'}`;
}

/** Plain-text export of the log for pasting into a ticket or chat. */
export function logToText(log) {
  if (!log) return '';
  const head = [`Run ${log.run_id || ''}  outcome=${log.outcome || ''}  updated=${log.updated_at || ''}`];
  const lines = (log.entries || []).map((e) => {
    const call = e.url ? `  [${e.url.method} ${e.url.url} → ${e.url.status ?? 'error'} ${e.url.duration_ms ?? ''}ms]` : '';
    return `${e.ts || ''} ${String(e.level || '').toUpperCase().padEnd(7)} [${e.stage}] ${e.message}${call}`;
  });
  return [...head, ...lines].join('\n');
}

/** "in / out" tokens plus prompt-cache reads, or '' when the call has none. */
export function tokensLabel(detail) {
  if (!detail || detail.input_tokens == null) return '';
  const base = `${detail.input_tokens} in / ${detail.output_tokens ?? 0} out`;
  return detail.cache_read_tokens ? `${base} · ${detail.cache_read_tokens} cached` : base;
}

const SOURCE_KINDS = ['chunk_source', 'chunk_cache', 'evidence_file', 'chunk_summary', 'evidence_routing'];

/** Entries that say where evidence came from and how it was read, in order. */
export function collectSources(entries) {
  return (entries || []).filter((e) => SOURCE_KINDS.includes(e.kind));
}

/** The backend's own step timings for loading the run (names, durations, row counts). */
export function collectBackendSteps(entries) {
  return (entries || [])
    .filter((e) => e.kind === 'db_timing')
    .map((e) => ({ step: e.detail?.step, duration_ms: e.detail?.duration_ms ?? 0, rows: e.detail?.rows }));
}

export function countStalls(entries) {
  return (entries || []).filter((e) => e.kind === 'loop_stall').length;
}
